import { Controller } from "@hotwired/stimulus"

// Status pill: click -> 6-option menu -> PATCH on selection -> in-place
// DOM update (no page reload, so scroll position, side panel state and the
// focused cell stay put). On multi-assignee tasks, the server returns the
// recomputed aggregate so the row's aggregate pill can sync.
//
// The menu is drawn ONCE per page (_status_pill_menus, owner 2026-10-15):
// a pill copies the right template (urgent or normal) into itself the
// first time it opens, and keeps it. Every editable pill used to carry its
// own hidden copy, about 45% of the Intern Tasks sheet's bytes.
//
// Cross-pill sync: when one pill updates, a `status:changed` event fires
// so any sibling pill showing the same completion or the same task's
// aggregate refreshes too (covers panel pill + row pill on the same
// page). A pill that was never opened takes its look from the template.
const menuCache = {}

export default class extends Controller {
  static targets = ["display", "dropdown"]
  static values = {
    url: String,
    field: { type: String, default: "status" },
    current: String,
    completionId: String,
    taskId: String,
    urgent: Boolean
  }

  connect() {
    this.closeOnOutsideClick = this.closeOnOutsideClick.bind(this)
    this.onStatusChanged = this.onStatusChanged.bind(this)
    this.onOtherOpened = this.onOtherOpened.bind(this)
    document.addEventListener("status:changed", this.onStatusChanged)
    document.addEventListener("status-pill:opened", this.onOtherOpened)
  }

  disconnect() {
    document.removeEventListener("click", this.closeOnOutsideClick)
    document.removeEventListener("status:changed", this.onStatusChanged)
    document.removeEventListener("status-pill:opened", this.onOtherOpened)
  }

  openDropdown(event) {
    event.stopPropagation()
    // Broadcast so any other open dropdown closes itself. stopPropagation
    // above kills the bubble path that the document-level outside-click
    // listener would have used, so cross-dropdown coordination has to be
    // explicit.
    document.dispatchEvent(new CustomEvent("status-pill:opened", { detail: { source: this } }))
    if (!this.hasDropdownTarget) {
      const menu = this.menuTemplate()
      if (!menu) return
      // Right after the display span, inside the same relative wrapper, so
      // the menu opens exactly where the per-pill copy used to.
      this.displayTarget.after(menu.firstElementChild.cloneNode(true))
    }
    this.dropdownTarget.hidden = false
    setTimeout(() => document.addEventListener("click", this.closeOnOutsideClick), 0)
  }

  onOtherOpened(event) {
    if (event.detail.source === this) return
    this.closeDropdown()
  }

  closeDropdown() {
    if (this.hasDropdownTarget) this.dropdownTarget.hidden = true
    document.removeEventListener("click", this.closeOnOutsideClick)
  }

  closeOnOutsideClick(event) {
    if (!this.element.contains(event.target)) this.closeDropdown()
  }

  async select(event) {
    event.stopPropagation()
    const optionEl = event.currentTarget
    const newStatus = optionEl.dataset.status
    this.closeDropdown()
    if (newStatus === this.currentValue) return

    try {
      // When this pill represents a specific completion (per-assignee
      // pill on the row or in the panel's Intern Notes), include the
      // completion_id so update_my_completion can target it directly
      // (required for non-intern staff editing another user's status).
      const body = { field: this.fieldValue, value: newStatus }
      if (this.completionIdValue) body.completion_id = this.completionIdValue

      const response = await fetch(this.urlValue, {
        method: "PATCH",
        headers: this.headers(),
        body: JSON.stringify(body)
      })
      if (!response.ok) throw new Error(`Status update failed: ${response.status}`)
      const data = await response.json().catch(() => ({}))

      this.applyStatus(newStatus, optionEl)

      // Broadcast for any sibling pill (row pill, panel pill, aggregate)
      // that needs to mirror this change.
      const taskRow = this.element.closest("[data-task-id]")
      const taskId = this.taskIdValue || taskRow?.dataset.taskId
      document.dispatchEvent(new CustomEvent("status:changed", {
        detail: {
          taskId: taskId,
          completionId: this.completionIdValue || null,
          newStatus: newStatus,
          aggregateStatus: data.aggregate_status || null,
          sourceEl: this.element
        }
      }))
    } catch (err) {
      console.error("status_pill save failed:", err)
      if (window.showToast) window.showToast("error", "That status didn't save. Try again.")
    }
  }

  // Pulls visual tokens (bg/text style + dot text + label text) from a
  // menu option that already has the right look: this pill's own copy of
  // the menu, or the page's template when it was never opened. Status
  // styling stays data-driven in ERB, with no hex codes duplicated in JS.
  applyStatus(newStatus, optionEl) {
    this.currentValue = newStatus
    let source = optionEl
    if (!source || source.dataset.status !== newStatus) {
      const menu = this.hasDropdownTarget ? this.dropdownTarget : this.menuTemplate()
      source = menu?.querySelector(`[data-status="${newStatus}"]`)
    }
    if (!source) return

    const styledSpan = source.querySelector("span[style]")
    const labelSpan = source.querySelector("span:last-child")
    const displaySpan = this.displayTarget.querySelector("span[style], .text-\\[10px\\]")
    const displayLabel = this.displayTarget.querySelector("span:last-child")

    // The pill's inline style (background + color) lives on the wrapper
    // span (this.displayTarget). The dot lives in its first child span.
    if (styledSpan) {
      this.displayTarget.setAttribute("style", styledSpan.getAttribute("style"))
      const dotEl = this.displayTarget.querySelector("span")
      if (dotEl) dotEl.textContent = styledSpan.textContent
    }
    if (labelSpan && displayLabel) {
      displayLabel.textContent = labelSpan.textContent
    }
  }

  onStatusChanged(event) {
    const { taskId, completionId, newStatus, aggregateStatus, sourceEl } = event.detail
    if (sourceEl === this.element) return // we triggered this; ignore

    const myTaskId = this.taskIdValue || this.element.closest("[data-task-id]")?.dataset.taskId
    if (!myTaskId || !taskId || String(myTaskId) !== String(taskId)) return

    const insideAggregate = !!this.element.closest(".status-aggregate")

    // Case A: I'm showing the same per-assignee completion that changed.
    if (completionId && this.completionIdValue === completionId) {
      this.applyStatus(newStatus, null)
      return
    }
    // Case B: task-level pill on the same task. Mirrors row<->panel for
    // single-assignee tasks where both surfaces edit task.status.
    if (!completionId && !this.completionIdValue && !insideAggregate) {
      this.applyStatus(newStatus, null)
      return
    }
    // Case C: aggregate pill on the row, and a per-assignee status just
    // changed. Sync to the new aggregate.
    if (aggregateStatus && !this.completionIdValue && insideAggregate) {
      this.applyStatus(aggregateStatus, null)
    }
  }

  // The page's menu template for this pill (urgent tasks keep Pending
  // red). Remembered once found: the templates never change.
  menuTemplate() {
    const kind = this.urgentValue ? "urgent" : "normal"
    menuCache[kind] ||= document.querySelector(`template[data-status-pill-menu="${kind}"]`)?.content
    return menuCache[kind]
  }

  headers() {
    const csrf = document.querySelector('meta[name="csrf-token"]')?.content
    return {
      "Content-Type": "application/json",
      "X-CSRF-Token": csrf,
      "Accept": "application/json"
    }
  }
}
